import { z } from 'zod';
import { ExaminationWorklistDTO } from '../models/worklist.models';
import { IdDescriptionItemSchema } from './iworklist.validation';

// Schema for PatientWorklistDTO
export const PatientWorklistDTOSchema = z
    .object({
        id: z.string(),
        surname: z.string().nullable(),
        name: z.string().nullable(),
        sex: z.string().nullable(),
        sexCode: z.string().nullable(),
        taxCode: z.string().nullable(),
        birthDate: z.string().nullable().optional(),
        race: z.string().nullable(),
        patientId: z.string(),
        issuer: z.string().nullable(),
        otherPatientId: z.string(),
        healthCard: z.string().nullable(),
        isAnonymous: z.boolean().nullable().optional(),
        isMerged: z.boolean(),
        phone: z.string().nullable(),
        email: z.string().nullable(),
        birthPlace: z.string().nullable(),
        residenceProvince: z.string().nullable(),
        residenceCity: z.string().nullable(),
        residenceAddress: z.string().nullable(),
        residenceZipCode: z.string().nullable(),
    })
    .strict();

// Schema for SeverityWorklistDTO
export const SeverityWorklistDTOSchema = IdDescriptionItemSchema.extend({
    color: z.string().nullable(),
}).strict();

// Schema for GroupProvenanceWorklistDTO
export const GroupProvenanceWorklistDTOSchema = IdDescriptionItemSchema.strict();

// Schema for ProvenanceWorklistDTO
export const ProvenanceWorklistDTOSchema = IdDescriptionItemSchema.extend({
    groupProvenance: GroupProvenanceWorklistDTOSchema,
}).strict();

// Schema for BusinessUnitWorklistDTO
export const BusinessUnitWorklistDTOSchema = IdDescriptionItemSchema.extend({
    httpPacs: z.string().nullable(),
    code: z.number(),
}).strict();

// Schema for UserWorklistDTO
export const UserWorklistDTOSchema = z
    .object({
        id: z.string(),
        fullName: z.string().nullable(),
    })
    .strict();

// Schema for CommandWorklistDTO
export const CommandWorklistDTOSchema = z
    .object({
        bookingCanBeModified: z.boolean(),
        canBePreAdmmitted: z.boolean(),
        canBeAdmmitted: z.boolean(),
        canBeUndoAdmmitted: z.boolean(),
        canBeExecuted: z.boolean(),
        canBeUndoExecuted: z.boolean(),
        canBeModified: z.boolean(),
        canBeExamModified: z.boolean(),
        canBeAddedNewExam: z.boolean(),
        canBeDeleted: z.boolean(),
        canBeSuspended: z.boolean(),
        canBeUndoSuspended: z.boolean(),
        canBeReported: z.boolean(),
        canReportBeModified: z.boolean(),
        canBeApproved: z.boolean(),
        canBeUndoApproved: z.boolean(),
        canBeDigitallySigned: z.boolean(),
        canBeDeferred: z.boolean(),
        canBeDeferredAndDelete: z.boolean(),
        canCDBeCreated: z.boolean(),
        canQuestionListBeOpened: z.boolean(),
        canCaseHistorySMBeOpened: z.boolean(),
        canCaseHistorySMXMLBeOpened: z.boolean(),
        canDraftBeOpened: z.boolean(),
        canReportBeOpened: z.boolean(),
        mustBeManualSentToWorklist: z.boolean(),
        isCollaborationActive: z.boolean(),
        canImagesBeViewed: z.boolean(),
        isMandatoryEquipmentChange: z.boolean(),
        canBePrintLabel: z.boolean(),
        canBeResigned: z.boolean(),
        canOpenConsultation: z.boolean(),
    })
    .strict();

// Schema for FeaDataWorklistDTO
export const FeaDataWorklistDTOSchema = z
    .object({
        hasPatientAuthorization: z.boolean(),
        isConsentValidated: z.boolean(),
    })
    .strict();

// Schema for AiData (imported from vnext.models)
export const AiDataSchema = z
    .object({
        alert: z.boolean(),
        clinicalApp: z.string().nullable(),
        score: z.string().nullable(),
        scoreValue: z.number().nullable(),
        status: z.string().nullable(),
    })
    .strict(); // Define based on actual AiData structure

// Main ExaminationWorklistDTO Schema
export const ExaminationWorklistDTOSchema = z
    .object({
        id: z.string(),
        idEvent: z.string(),
        idReport: z.string().nullable().optional(),
        plannedDate: z.string().nullable().optional(),
        admissionDate: z.string().nullable().optional(),
        executionDate: z.string().nullable().optional(),
        reportDate: z.string().nullable().optional(),
        approvalDate: z.string().nullable().optional(),
        lastDate: z.string().nullable().optional(),
        patient: PatientWorklistDTOSchema,
        exam: IdDescriptionItemSchema.nullable(), // ExamWorklistDTO extends IdDescriptionItem
        clinicalAlert: z.string().nullable(),
        status: z.string().nullable(),
        patientStatus: IdDescriptionItemSchema.nullable(), // PatientStatusWorklistDTO extends IdDescriptionItem
        severity: SeverityWorklistDTOSchema.nullable(),
        room: IdDescriptionItemSchema, // RoomWorklistDTO extends IdDescriptionItem
        equipment: IdDescriptionItemSchema.nullable(), // EquipmentWorklistDTO extends IdDescriptionItem
        provenance: ProvenanceWorklistDTOSchema.nullable(),
        ticket: z.boolean(),
        admissionNumber: z.string().nullable(),
        externalAdmissionNumber: z.string().nullable(),
        folderNumber: z.string().nullable(),
        hospitalizationNumber: z.string().nullable(),
        accessionNumber: z.string().nullable(),
        businessUnit: BusinessUnitWorklistDTOSchema,
        agenda: IdDescriptionItemSchema.nullable(), // AgendaWorklistDTO extends IdDescriptionItem
        technician: UserWorklistDTOSchema.nullable(),
        referringPhysician: UserWorklistDTOSchema.nullable(),
        secondReferringPhysician: UserWorklistDTOSchema.nullable(),
        referringPhysicianFullNameList: z.string().nullable(),
        requestingPhysician: UserWorklistDTOSchema.nullable(),
        reasonForStudy: z.string().nullable(),
        idCustomPage: z.string().nullable().optional(),
        idCustomPageRoot: z.string().nullable().optional(),
        idModelTypeRoots: z.array(z.string()),
        command: CommandWorklistDTOSchema,
        feaData: FeaDataWorklistDTOSchema,
        hasDose: z.boolean(),
        aiData: AiDataSchema,
        examinationDate: z.string().nullable().optional(),
        isExaminationLocked: z.number(),
        lockedHost: z.string().nullable(),
        idLockedUser: z.string().nullable().optional(),
        lockedUserFullName: z.string().nullable(),
        studyInstanceUid: z.string(),
        publicationAuthorization: z.boolean(),
        modality: IdDescriptionItemSchema, // ModalityWorklistDTO extends IdDescriptionItem
        deliveryLocation: IdDescriptionItemSchema.nullable(), // DeliveryLocationWorklistDTO extends IdDescriptionItem
        deliveryDate: z.string().nullable().optional(),
        reportingDeadline: z.number().nullable().optional(),
        reportedExaminationsNumber: z.number().nullable().optional(),
        revaluedCDA: z.boolean().nullable().optional(),
        isApproved: z.boolean(),
        isDigitallySigned: z.boolean(),
        imageAvailability: z.boolean(),
    })
    .strict();

export const validateExaminationWorklistDTO = (data: unknown): ExaminationWorklistDTO => {
    return ExaminationWorklistDTOSchema.parse(data) as ExaminationWorklistDTO;
};

export const applyExaminationWorklistValidation = (data: unknown, index: number) => {
    try {
        validateExaminationWorklistDTO(data);
    } catch (error: any) {
        console.error(`Validation failed for examination at index ${index}:`, error.errors);
        throw new Error(`Invalid examination data at index ${index}: ${error.message}`);
    }
};
